<template>
	<div class="header-user">
	<app-resizable-dropdown
		ref="dropdownRef"
		position="bottom"
		trigger="click"
		:fullscreen-on-mobile="false"
		content-class="header-user-dropdown"
	>
		<template #activator>
			<button
				type="button"
				class="user-trigger"
				:title="profile?.Name ?? ''"
			>
				<img
					v-if="profile?.PhotoLink"
					:class="[
						'user-trigger__avatar',
						'user-trigger__avatar--img',
						{ 'user-trigger__avatar--has-unread': hasUnreadItems }
					]"
					:src="resolvePhotoUrl(profile.PhotoLink)"
					alt=""
				/>
				<span
					v-else
					:class="[
						'user-trigger__avatar',
						'user-trigger__avatar--liter',
						{ 'user-trigger__avatar--has-unread': hasUnreadItems }
					]"
				>
					{{ avatarLiter }}
				</span>
				<span v-if="isMobile && unreadCount > 0" class="user-trigger__badge">
					{{ unreadCount }}
				</span>
			</button>
		</template>

		<div class="user-dd">
			<div
				class="user-dd__item"
				:class="isMobile ? 'user-dd__item--mobile-profile' : 'user-dd__item--clickable'"
				@click="!isMobile && onOpenSettings()"
			>
				<app-icon
					name="user"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<div class="user-dd__name">{{ profile?.Name || '—' }}</div>
					<div class="user-dd__email">{{ profile?.Mail || '' }}</div>
				</div>
			</div>

			<div
				:class="[
					'user-dd__item',
					editingStatus ? 'user-dd__item--editing' : 'user-dd__item--clickable'
				]"
				@click="!editingStatus && onEditStatus()"
				style="align-items: center; min-height: 34px;"
			>
				<app-icon
					name="comment"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<template v-if="!editingStatus">
						<span
							v-if="profile?.Status"
							class="user-dd__status-text"
						>
							{{ profile?.Status }}
						</span>
						<span
							v-else
							class="user-dd__status-placeholder"
						>
							{{ $t('status_placeholder') }}
						</span>
					</template>
					<template v-else>
						<div v-if="isCompactMode" class="user-dd__status-editor">
							<app-custom-textarea
								v-model="draftStatus"
								class="user-dd__status-textarea"
								:placeholder="$t('status_label')"
								:min-height="34"
								:max-height="260"
								auto-focus
								@ctrl-enter="onSaveStatus"
								@click.stop
							/>
							<button
								type="button"
								class="user-dd__status-save"
								@click.stop="onSaveStatus"
							>
								<app-icon
									name="check-round"
									width="18px"
									color="#D2D2D2"
									hover-color="#FFFFFF"
									cursor="pointer"
								/>
							</button>
						</div>
						<template v-else>
							<app-custom-textarea
								v-model="draftStatus"
								:placeholder="$t('status_label')"
								:min-height="80"
								:max-height="400"
								auto-focus
								:clear-hint-text="$t('status_clear')"
								@click.stop
							/>
							<div class="user-dd__status-actions">
								<app-button
									type="secondary"
									:label="$t('cancel_short')"
									@click="onCancelStatus"
								/>
								<app-button
									:label="$t('save')"
									@click="onSaveStatus"
								/>
							</div>
						</template>
					</template>
				</div>
			</div>

			<div
				v-if="isCompactMode"
				class="user-dd__item user-dd__item--clickable"
				@click="onOpenNotifications"
			>
				<app-icon
					name="burger"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<span class="user-dd__text">{{ notificationsLabel }}</span>
				</div>
			</div>

			<div
				v-if="isCompactMode && hasChatsAccess"
				class="user-dd__item user-dd__item--clickable"
				@click="onOpenMessages"
			>
				<app-icon
					name="envelope"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<span class="user-dd__text">{{ messagesLabel }}</span>
				</div>
			</div>
			<div
				v-else-if="isCompactMode && !hasChatsAccess"
				class="user-dd__item user-dd__item--clickable user-dd__item-invalid"
			>
				<app-icon
					name="envelope"
					width="18px"
					color="#979797"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<span class="user-dd__text" style="color: #979797">Нет доступа к чатам</span>
				</div>
			</div>

			<div
				class="user-dd__item user-dd__item--clickable"
				@click="onToggleSound"
			>
				<app-icon
					:name="soundIconName"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<span class="user-dd__text">{{ soundLabel }}</span>
				</div>
			</div>

			<div
				class="user-dd__item user-dd__item--clickable"
				@click="onLogout"
			>
				<app-icon
					name="logout"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<span class="user-dd__text">{{ $t('logout') }}</span>
				</div>
			</div>
		</div>
	</app-resizable-dropdown>
	</div>
</template>

<script setup lang="ts">
import { i18n } from "@/i18n";
import {
	computed,
	ref,
	watch
} from "vue"
import { useRouter } from "vue-router"
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue"
import AppCustomTextarea from "@/components/ui-kit/textarea/AppCustomTextarea.vue"
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue"
import AppButton from "@/components/ui-kit/button/AppButton.vue"
import { workerStore } from "@/store/worker"
import { headerStore } from "@/store/header"
import { logout } from "@/composables/useSession"
import { resolvePhotoUrl } from "@/utils/urlHelpers"
import { postToAngular, SETTINGS_SECTION_PROFILE } from "@/utils/angularEmbed"
import { useBreakpoints } from "@/composables/useBreakpoints"

const router = useRouter()

const wStore = workerStore()
const hStore = headerStore()
const { isMobile, isTablet } = useBreakpoints()
const isCompactMode = computed(() => isMobile.value || isTablet.value)

const dropdownRef = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const emit = defineEmits<{
	openNotifications: []
	openMessages: []
}>()

const profile = computed(() => wStore.workerProfile)
const notificationsCount = computed(() => hStore.notificationCount.JobCount || 0)
const messagesCount = computed(() => hStore.unreadMessagesCount || 0)
const hasChatsAccess = computed(() => hStore.chatsAccess !== false)
const unreadCount = computed(() =>
	notificationsCount.value + (hasChatsAccess.value ? messagesCount.value : 0)
)
const hasUnreadItems = computed(() => isMobile.value && unreadCount.value > 0)
const notificationsLabel = computed(() => notificationsCount.value
	? `Новых уведомлений: ${notificationsCount.value}`
	: 'Новых уведомлений нет'
)
const messagesLabel = computed(() => messagesCount.value
	? `Новых сообщений: ${messagesCount.value}`
	: 'Новых сообщений нет'
)

const onOpenSettings = () => {
	dropdownRef.value?.close()
	if (router.currentRoute.value.path === '/settings') {
		postToAngular({ type: 'settings_section', section: SETTINGS_SECTION_PROFILE })
		return
	}
	router.push('/settings')
}

const onOpenNotifications = () => {
	dropdownRef.value?.close()
	emit('openNotifications')
}

const onOpenMessages = () => {
	dropdownRef.value?.close()
	emit('openMessages')
}

const avatarLiter = computed(() => {
	const liter = profile.value?.PhotoLiter?.trim()
	if (liter) return liter
	const name = profile.value?.Name?.trim()
	return name ? name.charAt(0).toUpperCase() : '?'
})

const editingStatus = ref(false)
const draftStatus = ref<string>('')

watch(profile, (val) => {
	if (!editingStatus.value) {
		draftStatus.value = val?.Status ?? ''
	}
}, { immediate: true })

const onEditStatus = () => {
	draftStatus.value = profile.value?.Status ?? ''
	editingStatus.value = true
}

const onCancelStatus = () => {
	editingStatus.value = false
	draftStatus.value = profile.value?.Status ?? ''
}

const onSaveStatus = async () => {
	try {
		await wStore.setStatus(draftStatus.value)
		editingStatus.value = false
	} catch (e) {
		console.error('[HeaderUserDropdown] save status failed', e)
	}
}

const soundLabel = computed(() => String(i18n.global.t(hStore.enableSound ? 'sound_on' : 'sound_off')))

const soundIconName = computed(() => hStore.enableSound ? 'sound-on' : 'sound-off')

const onToggleSound = () => {
	hStore.toggleSound()
}

const onLogout = () => {
	logout({ rememberRoute: false })
}
</script>

<style scoped>
.header-user {
	display: inline-flex;
	align-items: center;
}

.user-trigger {
	display: inline-flex;
	align-items: center;
	position: relative;
	background: transparent;
	border: none;
	color: #fff;
	cursor: pointer;
	padding: 4px 0;
	font-family: inherit;
}

.user-trigger__avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	border: 2px solid #FFFFFF;
	box-sizing: border-box;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.user-trigger__avatar--has-unread {
	border-color: #3F884F;
}

.user-trigger__avatar--img {
	object-fit: cover;
}

.user-trigger__avatar--liter {
	background: #377444;
	color: #fff;
	font-size: 20px;
	font-weight: 600;
	line-height: 1;
}

.user-trigger__badge {
	position: absolute;
	top: -2px;
	right: -6px;
	z-index: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 20px;
	height: 20px;
	padding: 0 3px;
	box-sizing: border-box;
	border: 2px solid #3F884F;
	border-radius: 999px;
	background: #fff;
	color: #3F884F;
	font-size: 14px;
	font-weight: 600;
	line-height: 1;
	pointer-events: none;
}

.user-dd {
	display: flex;
	flex-direction: column;
	padding: 4px;
	background: #4D4D4D;
	color: #fff;
	width: 300px;
}

.user-dd__item {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	padding: 8px;
	border-radius: 4px;
	transition: background-color 0.1s;
}

.user-dd__item:hover {
	background-color: #5C5C5CE5;
}

.user-dd__item-invalid:hover {
	background-color: transparent;
}

.user-dd__item--clickable {
	cursor: pointer;
}

.user-dd__item--mobile-profile:hover {
	background-color: transparent;
}

.user-dd__item--mobile-profile {
	cursor: default;
}

.user-dd__item--editing:hover {
	background-color: transparent;
}

.user-dd__icon {
	flex-shrink: 0;
}

.user-dd__content {
	font-size: 14px;
    line-height: 18px;

	display: flex;
	flex-direction: column;
	min-width: 0;
	width: 100%;
}

.user-dd__name,
.user-dd__email,
.user-dd__status-text {
	overflow-wrap: anywhere;
}

.user-dd__name {
	font-size: 14px;
	line-height: 18px;
	font-weight: 500;
}

.user-dd__email {
	font-size: 12px;
	line-height: 18px;
	font-weight: 400;
}

.user-dd__status-text,
.user-dd__text {
	font-size: 14px;
	line-height: 18px;
	font-weight: 400;
}

.user-dd__status-placeholder {
	font-size: 14px;
	line-height: 18px;
	font-weight: 400;
	color: #D2D2D2;
}

.user-dd__status-editor {
	display: flex;
	gap: 8px;
	align-items: flex-start;
	width: 100%;
}

.user-dd__status-actions {
	display: flex;
	gap: 8px;
	justify-content: flex-end;
	margin-top: 4px;
}

.user-dd__content :deep(.app-custom-textarea) {
	background: #4D4D4D;
	color: #fff;
	border-color: rgba(255, 255, 255, 0.4);
}

.user-dd__content :deep(.app-custom-textarea::placeholder) {
	color: #D2D2D2;
}

.user-dd__status-textarea {
	flex: 1 1 auto;
	min-width: 0;
}

.user-dd__status-textarea :deep(.app-custom-textarea) {
	border-color: #3F884F;
}

.user-dd__status-save {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 18px;
	width: 18px;
	height: 18px;
	padding: 0;
	border: none;
	background: transparent;
	color: #D2D2D2;
	cursor: pointer;
	margin-top: 8px;
}

@media (max-width: 768px) {
	.user-dd {
		width: 280px;
		padding: 8px 8px 16px;
		box-sizing: border-box;
		gap: 8px;
	}

	.user-dd__item {
	min-height: 28px;
	display: flex;
	align-items: center;
	/* align-items: flex-start; */
	gap: 8px;
	/* padding: 8px; */
	padding: 0px 8px;
	border-radius: 4px;
	transition: background-color 0.1s;
	}
}
</style>

<style lang="scss">
@media (max-width: 768px) {
	.dropdown-content.header-user-dropdown {
		top: 56px !important;
		right: 0 !important;
		left: auto !important;
		width: 280px !important;
		height: auto !important;
		max-height: calc(100dvh - 56px) !important;
		background: #4D4D4D;
		border: none;
		border-radius: 0 0 16px 16px;
		box-shadow: 0 2px 4px #00000033;
	}
}
</style>
